import { useDraftSlot, usePendingMarker } from '@/shared/hooks/draftSlots';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type NomenclaturePriceRow } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';

/**
 * Закладка «Ціни» картки номенклатури (alembic 0260, 2026-06-08).
 *
 * Показує по рядку на КОЖЕН тип ціни tenant:
 * - Базовий (BASE) — ціна вводиться/редагується вручну (UPSERT на період).
 * - Динамічний (DYNAMIC) — ціна обчислюється на backend (computed_price,
 *   read-only). Користувач НЕ задає її тут — лише бачить.
 *
 * Розрахунок DYNAMIC робить backend (single source) — фронт лише показує
 * computed_price з відповіді (без дублювання формули → без дрейфу).
 */
const todayISO = () => new Date().toISOString().slice(0, 10);

export function NomenclaturePricesTab({ nomenclatureId }: { nomenclatureId: string }) {
  const confirm = useConfirm();
  const qc = useQueryClient();
  // ч.1060: ціна, що редагується, — у слоті КАРТКИ номенклатури (переживає
  // перемикання вкладок і входить у чернетку картки).
  const [editTypeId, setEditTypeId] = useDraftSlot<string | null>('prices.editTypeId', null);
  const [priceInput, setPriceInput] = useDraftSlot('prices.priceInput', '');
  usePendingMarker('prices', editTypeId ? 'ціна (не збережена)' : null);

  const key = ['nomenclature-prices', nomenclatureId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => refs.nomenclaturePrices.listByNomenclature(nomenclatureId),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const upsert = useMutation({
    mutationFn: ({ price_type_id, price }: { price_type_id: string; price: string }) =>
      refs.nomenclaturePrices.upsert({
        nomenclature_id: nomenclatureId, price_type_id: price_type_id,
        price, price_period: todayISO(),
      }),
    onSuccess: () => { setEditTypeId(null); setPriceInput(''); invalidate(); },
  });
  const remove = useMutation({
    mutationFn: (priceId: string) => refs.nomenclaturePrices.remove(priceId),
    onSuccess: invalidate,
  });

  const rows = list.data ?? [];

  const startEdit = (r: NomenclaturePriceRow) => {
    setEditTypeId(r.price_type_id);
    setPriceInput(r.price ?? '');
  };

  const fmt = (v: string | null) => (v == null ? '—' : Number(v).toFixed(2));

  return (
    <div>
      <p style={{ color: 'var(--muted)', maxWidth: 640 }}>
        Ціни за типами. <strong>Базові</strong> — вводите вручну.
        <strong> Динамічні</strong> рахуються автоматично від базового типу
        плюс наценка (їх не редагують тут).
      </p>
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Тип ціни</th>
            <th>Вид</th>
            <th style={{ textAlign: 'right' }}>Ціна</th>
            <th>Наценка</th>
            <th style={{ width: 160 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const editing = editTypeId === r.price_type_id;
            return (
              <tr key={r.price_type_id}>
                <td>{r.price_type_name}</td>
                <td style={{ textAlign: 'center' }}>{r.is_dynamic ? 'Динамічний' : 'Базовий'}</td>
                <td style={{ textAlign: 'right' }}>
                  {editing ? (
                    <input type="number" step="0.01" value={priceInput} autoFocus
                      onChange={(e) => setPriceInput(e.target.value)}
                      style={{ width: 110, textAlign: 'right' }} />
                  ) : (
                    fmt(r.computed_price)
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {r.is_dynamic && r.markup_percent != null ? `${Number(r.markup_percent)}%` : '—'}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {r.is_dynamic ? (
                    <span style={{ color: 'var(--muted)', fontSize: '0.85em' }}>обчислюється</span>
                  ) : editing ? (
                    <>
                      <button className="primary" disabled={priceInput === '' || upsert.isPending}
                        onClick={() => upsert.mutate({ price_type_id: r.price_type_id, price: priceInput })}>
                        Зберегти
                      </button>
                      <button style={{ marginLeft: '0.25rem' }} onClick={() => { setEditTypeId(null); setPriceInput(''); }}>
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <button title="Задати ціну" onClick={() => startEdit(r)}>✎</button>
                      {r.nomenclature_price_id && (
                        <button className="danger" title="Видалити ціну" style={{ marginLeft: '0.25rem' }}
                          onClick={() => confirm(`Видалити ціну «${r.price_type_name}»?`).then((ok) => {
                            if (ok && r.nomenclature_price_id) remove.mutate(r.nomenclature_price_id);
                          })}>✕</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
